import { useDeferredValue, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import {
    Alert,
    Avatar,
    Box,
    Button,
    ButtonBase,
    Card,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    Menu,
    MenuItem,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useChats, useContacts, useMarkAsSeen, useUnreadMessages } from 'hooks/useChat';
import { getPanelRole } from 'services/api';

const PATIENT_CATEGORY = 'patients';
const CLINICAL_CATEGORY = 'clinical';

const isPatientRole = (role) => role === 'PATIENT';

const matchesCategory = (role, category) =>
    category === PATIENT_CATEGORY ? isPatientRole(role) : !isPatientRole(role);

const formatConversationTime = (timestamp, t) => {
    const date = dayjs(timestamp);
    const today = dayjs();

    if (!date.isValid()) return '—';
    if (date.isSame(today, 'day')) return date.format('HH:mm');
    if (date.isSame(today.subtract(1, 'day'), 'day')) return t('messages.yesterday');
    if (date.isSame(today, 'year')) return date.format('DD/MM');
    return date.format('DD/MM/YYYY');
};

const getRoleLabel = (role, t) =>
    t(`messages.roles.${String(role || 'UNKNOWN').toLowerCase()}`, {
        defaultValue: t('messages.roles.unknown')
    });

const CategorySelector = ({ value, onChange, compact = false }) => {
    const { t } = useTranslation();

    return (
        <ToggleButtonGroup
            exclusive
            size="small"
            value={value}
            onChange={(_, nextValue) => nextValue && onChange(nextValue)}
            aria-label={t('messages.category_selector')}
            sx={{
                height: compact ? 34 : 46,
                p: 0.375,
                gap: 0.5,
                bgcolor: '#EEF2F5',
                borderRadius: 1.5,
                '& .MuiToggleButton-root': {
                    height: compact ? 27 : 39,
                    px: compact ? 1.25 : 2.5,
                    border: 0,
                    borderRadius: '5px !important',
                    color: '#53636F',
                    fontSize: compact ? '0.72rem' : '0.82rem',
                    fontWeight: 700,
                    textTransform: 'none'
                },
                '& .MuiToggleButton-root.Mui-selected': {
                    color: '#FFFFFF',
                    bgcolor: '#1976D2',
                    boxShadow: '0 1px 3px rgba(25, 118, 210, 0.25)'
                },
                '& .MuiToggleButton-root.Mui-selected:hover': { bgcolor: '#1565C0' }
            }}
        >
            <ToggleButton value={PATIENT_CATEGORY}>{t('messages.patients')}</ToggleButton>
            <ToggleButton value={CLINICAL_CATEGORY}>
                {t('messages.clinical_team')}
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

CategorySelector.propTypes = {
    value: PropTypes.oneOf([PATIENT_CATEGORY, CLINICAL_CATEGORY]).isRequired,
    onChange: PropTypes.func.isRequired,
    compact: PropTypes.bool
};

const MessageActions = ({ unseenUuids, contactName }) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { mutateAsync: markAsSeen, isPending } = useMarkAsSeen();

    const confirmMarkAsRead = async () => {
        if (!unseenUuids.length) return;
        await markAsSeen(unseenUuids).catch(() => undefined);
        setConfirmOpen(false);
    };

    return (
        <>
            {isPending ? (
                <CircularProgress size={18} />
            ) : (
                <Tooltip title={t('messages.conversation_actions')}>
                    <IconButton
                        size="small"
                        aria-label={t('messages.conversation_actions')}
                        onClick={(event) => setAnchorEl(event.currentTarget)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        setConfirmOpen(true);
                    }}
                >
                    {t('messages.mark_as_read')}
                </MenuItem>
            </Menu>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>
                    {t('messages.confirm_mark_title', { name: contactName })}
                </DialogTitle>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={confirmMarkAsRead} disabled={isPending} autoFocus>
                        {t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

MessageActions.propTypes = {
    unseenUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
    contactName: PropTypes.string.isRequired
};

const Messages = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const roleUuid = getPanelRole();
    const [category, setCategory] = useState(PATIENT_CATEGORY);
    const [unreadOnly, setUnreadOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [openNewChat, setOpenNewChat] = useState(false);
    const [contactCategory, setContactCategory] = useState(PATIENT_CATEGORY);
    const [contactSearch, setContactSearch] = useState('');
    const deferredContactSearch = useDeferredValue(contactSearch.trim());

    const chatsQuery = useChats(true);
    const unreadQuery = useUnreadMessages(roleUuid);
    const contactsQuery = useContacts(true, deferredContactSearch, openNewChat);

    const unreadByContact = useMemo(() => {
        const unreadMap = new Map();

        unreadQuery.data.forEach((message) => {
            const contactUuid = message.sender?.uuid;
            if (!contactUuid) return;

            const current = unreadMap.get(contactUuid) || { count: 0, uuids: [] };
            current.count += 1;
            current.uuids.push(message.uuid);
            unreadMap.set(contactUuid, current);
        });

        return unreadMap;
    }, [unreadQuery.data]);

    const conversations = useMemo(() => {
        const conversationMap = new Map();

        chatsQuery.data.forEach((message) => {
            const isMine = message.sender?.uuid === roleUuid;
            const contact = isMine ? message.receiver : message.sender;
            if (!contact?.uuid || contact.uuid === roleUuid) return;

            const existing = conversationMap.get(contact.uuid);
            if (!existing || new Date(message.sent_at) > new Date(existing.sentAt)) {
                const unread = unreadByContact.get(contact.uuid) || {
                    count: 0,
                    uuids: []
                };
                conversationMap.set(contact.uuid, {
                    contact,
                    lastMessage: message.message,
                    sentAt: message.sent_at,
                    unreadCount: unread.count,
                    unseenUuids: unread.uuids
                });
            }
        });

        return Array.from(conversationMap.values()).sort(
            (first, second) => new Date(second.sentAt) - new Date(first.sentAt)
        );
    }, [chatsQuery.data, roleUuid, unreadByContact]);

    const categoryConversations = useMemo(
        () =>
            conversations.filter((conversation) =>
                matchesCategory(conversation.contact.role, category)
            ),
        [category, conversations]
    );

    const unreadCount = useMemo(
        () =>
            categoryConversations.reduce(
                (total, conversation) => total + conversation.unreadCount,
                0
            ),
        [categoryConversations]
    );

    const displayedConversations = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLocaleLowerCase();

        return categoryConversations.filter((conversation) => {
            if (unreadOnly && conversation.unreadCount === 0) return false;
            if (!normalizedSearch) return true;

            return [conversation.contact.name, conversation.lastMessage]
                .filter(Boolean)
                .some((value) => value.toLocaleLowerCase().includes(normalizedSearch));
        });
    }, [categoryConversations, searchTerm, unreadOnly]);

    const availableContacts = useMemo(
        () =>
            contactsQuery.data
                .filter(
                    (contact) =>
                        contact.uuid !== roleUuid &&
                        matchesCategory(contact.role, contactCategory)
                )
                .sort((first, second) => first.name.localeCompare(second.name)),
        [contactCategory, contactsQuery.data, roleUuid]
    );

    const openContactDialog = () => {
        setContactCategory(category);
        setContactSearch('');
        setOpenNewChat(true);
    };

    const openConversation = (contact) =>
        navigate(`/mensagens/${contact.uuid}`, { state: { contact } });

    const loading = chatsQuery.isLoading || unreadQuery.isLoading;

    return (
        <Box sx={{ minHeight: 'calc(100vh - 80px)', p: 2, bgcolor: '#F4F6F8' }}>
            <Box
                component="header"
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'stretch', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2
                }}
            >
                <CategorySelector value={category} onChange={setCategory} />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openContactDialog}
                    sx={{
                        minWidth: 170,
                        height: 46,
                        borderRadius: 1.5,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        textTransform: 'none'
                    }}
                >
                    {t('messages.new_message')}
                </Button>
            </Box>

            <Card
                component="section"
                variant="outlined"
                sx={{
                    overflow: 'hidden',
                    borderColor: '#E0E5E9',
                    borderRadius: 2,
                    boxShadow: '0 1px 3px rgba(38, 50, 56, 0.08)'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2
                    }}
                >
                    <TextField
                        size="small"
                        value={searchTerm}
                        placeholder={t('messages.search_conversations')}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        sx={{ width: { xs: '100%', sm: 330 } }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />

                    <Button
                        size="small"
                        variant={unreadOnly ? 'contained' : 'outlined'}
                        startIcon={<FilterListIcon />}
                        onClick={() => setUnreadOnly((current) => !current)}
                        aria-pressed={unreadOnly}
                        sx={{
                            minWidth: 106,
                            height: 36,
                            fontWeight: 700,
                            textTransform: 'none'
                        }}
                    >
                        {t('messages.unread_filter')}
                        {unreadCount > 0 && (
                            <Box
                                component="span"
                                sx={{
                                    display: 'grid',
                                    minWidth: 20,
                                    height: 20,
                                    ml: 0.75,
                                    px: 0.5,
                                    placeItems: 'center',
                                    color: unreadOnly ? '#1976D2' : '#FFFFFF',
                                    bgcolor: unreadOnly ? '#FFFFFF' : '#1976D2',
                                    borderRadius: '50%',
                                    fontSize: '0.68rem',
                                    fontWeight: 700
                                }}
                            >
                                {unreadCount}
                            </Box>
                        )}
                    </Button>
                </Box>

                <Divider />

                {loading ? (
                    <Box sx={{ display: 'grid', minHeight: 320, placeItems: 'center' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : chatsQuery.error ? (
                    <Box
                        sx={{
                            display: 'flex',
                            minHeight: 280,
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            gap: 1
                        }}
                    >
                        <Typography color="text.secondary">
                            {t('messages.load_error')}
                        </Typography>
                        <Button onClick={() => chatsQuery.refetch()}>
                            {t('dashboard.home.retry')}
                        </Button>
                    </Box>
                ) : (
                    <>
                        {unreadQuery.isError && (
                            <Alert severity="warning" sx={{ m: 2, mb: 0 }}>
                                {t('messages.unread_load_error')}
                            </Alert>
                        )}

                        {displayedConversations.length === 0 ? (
                            <Box
                                sx={{
                                    display: 'grid',
                                    minHeight: 280,
                                    placeItems: 'center'
                                }}
                            >
                                <Typography color="text.secondary">
                                    {t(
                                        unreadOnly
                                            ? 'messages.no_unread_conversations'
                                            : 'messages.no_conversations'
                                    )}
                                </Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {displayedConversations.map((conversation, index) => {
                                    const { contact } = conversation;
                                    const unread = conversation.unreadCount > 0;

                                    return (
                                        <Box key={contact.uuid}>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                        'minmax(0, 1fr) 40px',
                                                    alignItems: 'center',
                                                    bgcolor: unread
                                                        ? '#F3F8FE'
                                                        : '#FFFFFF',
                                                    transition:
                                                        'background-color 160ms ease',
                                                    '&:hover': { bgcolor: '#F7FAFD' }
                                                }}
                                            >
                                                <ButtonBase
                                                    onClick={() =>
                                                        openConversation(contact)
                                                    }
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns:
                                                            '48px minmax(0, 1fr) auto',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        minWidth: 0,
                                                        minHeight: 76,
                                                        px: 2,
                                                        py: 1,
                                                        textAlign: 'left'
                                                    }}
                                                >
                                                    <Avatar
                                                        sx={{
                                                            width: 42,
                                                            height: 42,
                                                            bgcolor: unread
                                                                ? '#1976D2'
                                                                : '#DCE8F3',
                                                            color: unread
                                                                ? '#FFFFFF'
                                                                : '#31546F',
                                                            fontSize: '0.9rem',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {contact.name?.[0]?.toUpperCase() ||
                                                            '?'}
                                                    </Avatar>

                                                    <Box sx={{ minWidth: 0 }}>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'baseline',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <Typography
                                                                noWrap
                                                                sx={{
                                                                    color: '#20262D',
                                                                    fontSize: '0.9rem',
                                                                    fontWeight: unread
                                                                        ? 700
                                                                        : 600
                                                                }}
                                                            >
                                                                {contact.name ||
                                                                    t('messages.no_name')}
                                                            </Typography>
                                                            <Typography
                                                                noWrap
                                                                color="text.secondary"
                                                                sx={{
                                                                    fontSize: '0.68rem'
                                                                }}
                                                            >
                                                                {getRoleLabel(
                                                                    contact.role,
                                                                    t
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                        <Typography
                                                            noWrap
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.25,
                                                                fontSize: '0.79rem',
                                                                fontWeight: unread
                                                                    ? 600
                                                                    : 400
                                                            }}
                                                        >
                                                            {conversation.lastMessage ||
                                                                '—'}
                                                        </Typography>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            minWidth: 58,
                                                            alignItems: 'flex-end',
                                                            flexDirection: 'column',
                                                            gap: 0.75
                                                        }}
                                                    >
                                                        <Typography
                                                            color="text.secondary"
                                                            sx={{ fontSize: '0.7rem' }}
                                                        >
                                                            {formatConversationTime(
                                                                conversation.sentAt,
                                                                t
                                                            )}
                                                        </Typography>
                                                        {unread && (
                                                            <Box
                                                                aria-label={t(
                                                                    'messages.unread_count',
                                                                    {
                                                                        count: conversation.unreadCount
                                                                    }
                                                                )}
                                                                sx={{
                                                                    display: 'grid',
                                                                    minWidth: 22,
                                                                    height: 22,
                                                                    px: 0.6,
                                                                    placeItems: 'center',
                                                                    color: '#FFFFFF',
                                                                    bgcolor: '#1976D2',
                                                                    borderRadius: 3,
                                                                    fontSize: '0.68rem',
                                                                    fontWeight: 700
                                                                }}
                                                            >
                                                                {conversation.unreadCount}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </ButtonBase>

                                                {unread ? (
                                                    <MessageActions
                                                        unseenUuids={
                                                            conversation.unseenUuids
                                                        }
                                                        contactName={
                                                            contact.name ||
                                                            t('messages.no_name')
                                                        }
                                                    />
                                                ) : (
                                                    <Box />
                                                )}
                                            </Box>
                                            {index <
                                                displayedConversations.length - 1 && (
                                                <Divider />
                                            )}
                                        </Box>
                                    );
                                })}
                            </List>
                        )}
                    </>
                )}
            </Card>

            <Dialog
                open={openNewChat}
                onClose={() => setOpenNewChat(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ pb: 1.5, fontSize: '1.1rem', fontWeight: 700 }}>
                    {t('messages.select_contact')}
                </DialogTitle>
                <Box sx={{ px: 3, pb: 2 }}>
                    <CategorySelector
                        compact
                        value={contactCategory}
                        onChange={setContactCategory}
                    />
                    <TextField
                        fullWidth
                        size="small"
                        value={contactSearch}
                        placeholder={t('messages.search_contacts')}
                        onChange={(event) => setContactSearch(event.target.value)}
                        sx={{ mt: 1.5 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                <Divider />

                <Box sx={{ minHeight: 260, maxHeight: 430, overflowY: 'auto' }}>
                    {contactsQuery.isLoading ? (
                        <Box sx={{ display: 'grid', height: 260, placeItems: 'center' }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : contactsQuery.error ? (
                        <Box
                            sx={{
                                display: 'flex',
                                height: 260,
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 1
                            }}
                        >
                            <Typography color="text.secondary">
                                {t('messages.contacts_load_error')}
                            </Typography>
                            <Button onClick={() => contactsQuery.refetch()}>
                                {t('dashboard.home.retry')}
                            </Button>
                        </Box>
                    ) : availableContacts.length === 0 ? (
                        <Box sx={{ display: 'grid', height: 260, placeItems: 'center' }}>
                            <Typography color="text.secondary">
                                {t('messages.no_contacts')}
                            </Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {availableContacts.map((contact, index) => (
                                <Box key={contact.uuid}>
                                    <ButtonBase
                                        onClick={() => {
                                            setOpenNewChat(false);
                                            openConversation(contact);
                                        }}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '42px minmax(0, 1fr)',
                                            alignItems: 'center',
                                            gap: 1.25,
                                            width: '100%',
                                            minHeight: 62,
                                            px: 3,
                                            py: 1,
                                            textAlign: 'left',
                                            '&:hover': { bgcolor: '#F7FAFD' }
                                        }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: '#DCE8F3',
                                                color: '#31546F',
                                                fontSize: '0.8rem',
                                                fontWeight: 700
                                            }}
                                        >
                                            {contact.name?.[0]?.toUpperCase() || '?'}
                                        </Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography
                                                noWrap
                                                sx={{
                                                    fontSize: '0.86rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {contact.name || t('messages.no_name')}
                                            </Typography>
                                            <Typography
                                                color="text.secondary"
                                                sx={{ fontSize: '0.72rem' }}
                                            >
                                                {getRoleLabel(contact.role, t)}
                                            </Typography>
                                        </Box>
                                    </ButtonBase>
                                    {index < availableContacts.length - 1 && <Divider />}
                                </Box>
                            ))}
                        </List>
                    )}
                </Box>

                <DialogActions>
                    <Button onClick={() => setOpenNewChat(false)}>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Messages;
